import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { localStore } from '../../../v2/js/utils/storage';
import { UserTypeEnum } from '../../api';
import { SessionContext } from '../../app/contexts/SessionContext';

import './Login.scss';
import { useDocumentTitle } from '../../app/hooks/use-document-title';
import { LoginForm } from './LoginForm';

export const Login = () => {
  useDocumentTitle('Login');
  const navigate = useNavigate();
  const location = useLocation();
  const [showSessionExpiredMessage, setShowSessionExpiredMessage] = useState(false);

  // Get the 'next' parameter from the URL
  const params = new URLSearchParams(location.search);
  let nextUrl = params.get('next');
  const sessionExpired = params.get('session_expired');

  // Handle nested next parameters (decode and extract the real destination)
  if (nextUrl) {
    try {
      const decodedNext = decodeURIComponent(nextUrl);

      // If the decoded next URL is another login URL, extract the actual destination
      if (decodedNext.startsWith('/login?')) {
        const nestedParams = new URLSearchParams(decodedNext.split('?')[1]);
        const actualNext = nestedParams.get('next');
        if (actualNext) {
          nextUrl = actualNext;
        }
      }
    } catch (e) {
      console.error('Error decoding next URL:', e);
      nextUrl = null;
    }
    nextUrl = nextUrl || '';
    localStore.set('sessionContinueUrl', nextUrl);
  }

  // Build Google OAuth URL
  const googleAuthUrl = () => {
    return `${window.GOOGLE_LOGIN_REDIRECT_URL}?user_type=${UserTypeEnum.PROVIDER}`;
  };

  // Setup/retrieve necessary state
  const { user } = useContext(SessionContext);

  // Handle session expired message
  useEffect(() => {
    if (sessionExpired === 'true') {
      setShowSessionExpiredMessage(true);

      // Remove the session_expired parameter from URL without triggering navigation
      const newParams = new URLSearchParams(location.search);
      newParams.delete('session_expired');
      const newSearch = newParams.toString();
      const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');

      // Replace the current URL without the session_expired parameter
      window.history.replaceState(null, '', newUrl);
    }
  }, [sessionExpired, location.pathname, location.search]);

  useEffect(() => {
    if (user) {
      // If there's a next URL, decode and navigate to it
      if (nextUrl) {
        navigate(decodeURIComponent(nextUrl));
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate, nextUrl]);

  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <LoginForm showSessionExpiredMessage={showSessionExpiredMessage} />
            <div className="separator">
              <span>or continue with</span>
            </div>
            <div className="social-buttons">
              <Button
                variant="outline-primary"
                size="lg"
                className="d-flex align-items-center justify-content-center w-100"
                href={googleAuthUrl()}
              >
                <FcGoogle className="me-2" />
                <span>Log in with Google</span>
              </Button>
            </div>
            <div className="create-account">
              Don&apos;t have an account yet?{' '}
              <span className="d-block d-md-inline">
                <Link to="/register/provider/setup">Provider Sign Up</Link>
              </span>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
